import React, { useState } from 'react';
import { DIFFICULTY_OPTIONS } from '../constants/recipe';

export interface RecipeFormIngredient {
  name: string;
  quantity: number;
  unit: string;
}

export interface RecipeFormValues {
  name: string;
  cuisine: string;
  difficulty: string;
  prep_time: number;
  cook_time: number;
  servings: number;
  ingredients: RecipeFormIngredient[];
  instructions: string;
}

interface Props {
  title: string;
  hint?: string;
  initialValues: RecipeFormValues;
  submitting: boolean;
  onSave: (values: RecipeFormValues) => void;
  onCancel: () => void;
}

const emptyIngredient: RecipeFormIngredient = { name: '', quantity: 0, unit: '' };

const RecipeFormModal: React.FC<Props> = ({ title, hint, initialValues, submitting, onSave, onCancel }) => {
  const [values, setValues] = useState<RecipeFormValues>(initialValues);

  const updateIngredient = (index: number, patch: Partial<RecipeFormIngredient>) => {
    setValues((prev) => ({
      ...prev,
      ingredients: prev.ingredients.map((ing, i) => (i === index ? { ...ing, ...patch } : ing)),
    }));
  };

  const addIngredient = () => {
    setValues((prev) => ({ ...prev, ingredients: [...prev.ingredients, { ...emptyIngredient }] }));
  };

  const removeIngredient = (index: number) => {
    setValues((prev) => ({ ...prev, ingredients: prev.ingredients.filter((_, i) => i !== index) }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!values.name.trim()) return;
    onSave({
      ...values,
      ingredients: values.ingredients.filter((ing) => ing.name.trim()),
    });
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content recipe-form-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onCancel}>✕</button>

        <h2>{title}</h2>
        {hint && <p className="scan-review-hint">{hint}</p>}

        <form onSubmit={handleSubmit} className="add-item-form">
          <div className="form-group">
            <label>Recipe Name *</label>
            <input
              type="text"
              value={values.name}
              onChange={(e) => setValues((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Grandma's Banana Bread"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Cuisine</label>
              <input
                type="text"
                value={values.cuisine}
                onChange={(e) => setValues((prev) => ({ ...prev, cuisine: e.target.value }))}
                placeholder="e.g., Italian"
              />
            </div>
            <div className="form-group">
              <label>Difficulty</label>
              <select
                value={values.difficulty}
                onChange={(e) => setValues((prev) => ({ ...prev, difficulty: e.target.value }))}
              >
                {DIFFICULTY_OPTIONS.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row form-row-3">
            <div className="form-group">
              <label>Prep (min)</label>
              <input
                type="number"
                min={0}
                value={values.prep_time}
                onChange={(e) => setValues((prev) => ({ ...prev, prep_time: parseInt(e.target.value, 10) || 0 }))}
              />
            </div>
            <div className="form-group">
              <label>Cook (min)</label>
              <input
                type="number"
                min={0}
                value={values.cook_time}
                onChange={(e) => setValues((prev) => ({ ...prev, cook_time: parseInt(e.target.value, 10) || 0 }))}
              />
            </div>
            <div className="form-group">
              <label>Servings</label>
              <input
                type="number"
                min={1}
                value={values.servings}
                onChange={(e) => setValues((prev) => ({ ...prev, servings: parseInt(e.target.value, 10) || 1 }))}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Ingredients</label>
            <div className="ingredient-rows">
              {values.ingredients.map((ing, index) => (
                <div key={index} className="ingredient-row">
                  <input
                    type="text"
                    className="ingredient-name"
                    value={ing.name}
                    onChange={(e) => updateIngredient(index, { name: e.target.value })}
                    placeholder="Ingredient name"
                  />
                  <input
                    type="number"
                    step="0.1"
                    className="ingredient-quantity"
                    value={ing.quantity}
                    onChange={(e) => updateIngredient(index, { quantity: parseFloat(e.target.value) || 0 })}
                    placeholder="Qty"
                  />
                  <input
                    type="text"
                    className="ingredient-unit"
                    value={ing.unit}
                    onChange={(e) => updateIngredient(index, { unit: e.target.value })}
                    placeholder="Unit"
                  />
                  <button
                    type="button"
                    className="ingredient-remove"
                    onClick={() => removeIngredient(index)}
                    aria-label="Remove ingredient"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            <button type="button" className="btn-secondary" onClick={addIngredient}>
              + Add Ingredient
            </button>
          </div>

          <div className="form-group">
            <label>Instructions</label>
            <textarea
              className="recipe-instructions-input"
              value={values.instructions}
              onChange={(e) => setValues((prev) => ({ ...prev, instructions: e.target.value }))}
              placeholder="Step by step instructions..."
              rows={6}
            />
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={submitting || !values.name.trim()} className="btn-primary">
              {submitting ? 'Saving...' : 'Save Recipe'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecipeFormModal;
